// ─────────────────────────────────────────────────────────────────────────────
// Fit-to-page print helper.
// Captures a DOM element's HTML and prints it scaled to fit a chosen paper size
// (A4 / A3, portrait / landscape). Used by the WBS tree and the Gantt chart so a
// fully-expanded diagram fits cleanly on the page instead of overflowing.
// ─────────────────────────────────────────────────────────────────────────────

// Printable area (mm) for each paper size/orientation, after a small margin.
const PAPER_MM = {
  A4: { portrait: { w: 210, h: 297 }, landscape: { w: 297, h: 210 } },
  A3: { portrait: { w: 297, h: 420 }, landscape: { w: 420, h: 297 } },
};
const MARGIN_MM = 8;
const MM_TO_PX = 96 / 25.4; // CSS px per mm at 96dpi

/**
 * @param {Object} opts
 *   el         - DOM element to print (its outerHTML is captured)
 *   title      - document title + heading
 *   subtitle   - small line under the heading
 *   paper      - 'A4' | 'A3'
 *   orientation- 'portrait' | 'landscape'
 *   fitMode    - 'width' (scale to page width, may flow down pages) |
 *                'page'  (scale so the whole thing fits one page)
 *   extraCss   - optional extra CSS string
 */
export function printElementFitted({ el, title = 'Export', subtitle = '', paper = 'A3', orientation = 'landscape', fitMode = 'page', extraCss = '' }) {
  if (!el) return;
  const dims = (PAPER_MM[paper] || PAPER_MM.A3)[orientation] || PAPER_MM.A3.landscape;
  const pageW_mm = dims.w - MARGIN_MM * 2;
  const pageH_mm = dims.h - MARGIN_MM * 2;
  const pageW_px = pageW_mm * MM_TO_PX;
  const pageH_px = pageH_mm * MM_TO_PX;

  // Natural content size
  const contentW = el.scrollWidth || el.offsetWidth || pageW_px;
  const contentH = el.scrollHeight || el.offsetHeight || pageH_px;

  // Scale factor
  let scale;
  if (fitMode === 'page') {
    scale = Math.min(pageW_px / contentW, pageH_px / contentH, 1);
  } else {
    scale = Math.min(pageW_px / contentW, 1); // fit width; height flows
  }
  // Don't shrink below a readable floor; if it would, fit width instead.
  if (scale < 0.25 && fitMode === 'page') {
    scale = Math.min(pageW_px / contentW, 1);
  }

  const date = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' });
  const captured = el.outerHTML;

  const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${title}</title>
<style>
  * { -webkit-print-color-adjust:exact!important; print-color-adjust:exact!important; box-sizing:border-box; }
  html,body { margin:0; padding:0; background:#fff; font-family:Arial,Helvetica,sans-serif; }
  .pf-page { padding:${MARGIN_MM}mm; }
  .pf-head { margin:0 0 2mm; }
  .pf-head h1 { font-size:16px; margin:0 0 2px; color:#1E293B; }
  .pf-head .sub { font-size:10px; color:#8A93A8; }
  .pf-scale-wrap { transform-origin: top left; transform: scale(${scale}); width:${contentW}px; }
  svg line, svg path { stroke:#CBD5E1; }
  /* Keep connector bars/borders visible when printing */
  div[style*="background:#CBD5E1"], div[style*="background: #CBD5E1"] { background:#CBD5E1!important; }
  .pf-toolbar { position:fixed; top:10px; right:10px; z-index:9999; }
  .pf-toolbar button { padding:8px 16px; background:#5B5BD6; color:#fff; border:none; border-radius:6px; cursor:pointer; font-size:13px; font-weight:700; }
  @media print {
    .pf-toolbar { display:none; }
    @page { size:${paper} ${orientation}; margin:0; }
  }
  ${extraCss}
</style></head><body>
  <div class="pf-toolbar"><button onclick="window.print()">🖨️ Print / Save as PDF</button></div>
  <div class="pf-page">
    <div class="pf-head"><h1>${title}</h1>${subtitle ? `<div class="sub">${subtitle} · Exported ${date}</div>` : ''}</div>
    <div class="pf-scale-wrap">${captured}</div>
  </div>
</body></html>`;

  const iframe = document.createElement('iframe');
  iframe.style.cssText = 'position:fixed;top:0;left:0;width:100%;height:100%;border:none;z-index:99999;background:white';
  document.body.appendChild(iframe);
  const doc = iframe.contentDocument;
  doc.open(); doc.write(html); doc.close();
  setTimeout(() => {
    iframe.contentWindow.focus();
    iframe.contentWindow.print();
    setTimeout(() => { try { document.body.removeChild(iframe); } catch (_) {} }, 1500);
  }, 700);
}
